import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../store/hooks'
import { signedOut } from '../../features/auth/authSlice'
import { useSignOutMutation } from '../../services/authApi'
import { useSession } from '../auth/useSession'
import { cn } from '../../utils/cn'

const TABS = [
  { to: '', label: 'My bookings' },
  { to: 'profile', label: 'Profile settings' },
]

/**
 * The account area frame: editorial welcome, a slab side navigation and the
 * section content, exactly as `my_account_*` composes it — a 12-column grid with
 * a 3-column rail that sticks under the app bar on desktop and turns into a
 * horizontally scrolling row on mobile.
 */
export function AccountLayout() {
  const { slug, customer } = useSession()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [signOut, { isLoading }] = useSignOutMutation()

  const leave = async () => {
    // The server call is best-effort: the local token is cleared either way, so a
    // customer on a shared device is never left signed in by a network failure.
    await signOut(slug)
    dispatch(signedOut(slug))
    navigate(`/${slug}`, { replace: true })
  }

  const firstName = customer?.first_name || customer?.name || ''

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'block whitespace-nowrap rounded-lg px-6 py-4 font-label-caps text-label-caps uppercase transition-colors duration-300 md:w-full',
      isActive
        ? 'bg-primary-container text-on-primary-container'
        : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary',
    )

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <header className="mb-12 text-center md:text-left">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          {firstName ? `Welcome back, ${firstName}` : 'My account'}
        </h1>
        <p className="max-w-2xl font-body-lg text-body-lg text-on-surface-variant md:mx-0">
          See your upcoming appointments, look back at past visits, and keep your details up to date.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <aside className="md:col-span-3">
          <div className="flex flex-row gap-2 overflow-x-auto pb-4 md:sticky md:top-32 md:flex-col md:pb-0">
            <nav aria-label="Account sections" className="flex flex-row gap-2 md:flex-col">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to ? `/${slug}/account/${tab.to}` : `/${slug}/account`}
                  end={tab.to === ''}
                  className={tabClass}
                >
                  {tab.label}
                </NavLink>
              ))}
            </nav>

            <div className="md:mt-6 md:border-t md:border-primary/10 md:pt-6">
              <button
                type="button"
                onClick={() => void leave()}
                disabled={isLoading}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg px-6 py-4 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors duration-300 hover:bg-primary/5 hover:text-primary disabled:opacity-40 md:w-full"
              >
                <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
                  logout
                </span>
                Sign out
              </button>
            </div>
          </div>
        </aside>

        <div className="md:col-span-9">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
